import { SCREEN_HEIGHT, SCREEN_WIDTH } from "../worker/protocol";

export type PaletteName = "native" | "dmg-green" | "dmg-grey" | "pocket";

/** Lightest → darkest, as 0xRRGGBB. Used only for the optional DMG remaps. */
const PALETTES: Record<Exclude<PaletteName, "native">, [number, number, number, number]> = {
  "dmg-green": [0x9bbc0f, 0x8bac0f, 0x306230, 0x0f380f],
  "dmg-grey": [0xffffff, 0xaaaaaa, 0x555555, 0x000000],
  pocket: [0xc4cfa1, 0x8b956d, 0x4d533c, 0x1f1f1f],
};

export const PALETTE_LABELS: Record<PaletteName, string> = {
  native: "Color",
  "dmg-green": "DMG green",
  "dmg-grey": "DMG grey",
  pocket: "Pocket",
};

const PALETTE_KEY = "redchain.palette";
/** Bump when a release should force Color (native) over a stored DMG remap. */
const PALETTE_GEN_KEY = "redchain.palette.gen";
const PALETTE_GEN = "3";

/** 0xRRGGBB → little-endian RGBA word as ImageData expects (0xAABBGGRR). */
function toAbgr(rgb: number): number {
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = rgb & 0xff;
  return (0xff << 24) | (b << 16) | (g << 8) | r;
}

function loadPalettePreference(): PaletteName {
  if (localStorage.getItem(PALETTE_GEN_KEY) !== PALETTE_GEN) {
    localStorage.setItem(PALETTE_GEN_KEY, PALETTE_GEN);
    localStorage.setItem(PALETTE_KEY, "native");
    return "native";
  }
  const stored = localStorage.getItem(PALETTE_KEY) as PaletteName | null;
  if (stored && stored in PALETTE_LABELS) return stored;
  return "native";
}

/**
 * Draws 160×144 frames with integer scaling and nearest-neighbour filtering (§7.1).
 * The core emits true-color CGB pixels (boot-ROM / game palettes). "native" copies them
 * through; the DMG options collapse luminance to a four-shade display palette.
 */
export class Renderer {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly image: ImageData;
  private readonly out: Uint32Array;
  private lut: Uint32Array | null;
  private palette: PaletteName;
  private resizeObserver: ResizeObserver;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly container: HTMLElement,
  ) {
    canvas.width = SCREEN_WIDTH;
    canvas.height = SCREEN_HEIGHT;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) throw new Error("2D canvas unavailable");
    ctx.imageSmoothingEnabled = false;
    this.ctx = ctx;
    this.image = ctx.createImageData(SCREEN_WIDTH, SCREEN_HEIGHT);
    this.out = new Uint32Array(this.image.data.buffer);
    this.palette = loadPalettePreference();
    this.lut = this.buildLut(this.palette);
    this.resizeObserver = new ResizeObserver(() => this.fit());
    this.resizeObserver.observe(container);
    this.fit();
  }

  private buildLut(name: PaletteName): Uint32Array | null {
    if (name === "native") return null;
    const p = PALETTES[name];
    // Index by the red channel's top two bits: 0xFF → lightest, 0x00 → darkest.
    const lut = new Uint32Array(4);
    for (let i = 0; i < 4; i++) lut[i] = toAbgr(p[3 - i] ?? 0);
    return lut;
  }

  setPalette(name: PaletteName): void {
    this.palette = name;
    this.lut = this.buildLut(name);
    localStorage.setItem(PALETTE_KEY, name);
  }

  getPalette(): PaletteName {
    return this.palette;
  }

  draw(pixels: ArrayBuffer): void {
    const src = new Uint32Array(pixels);
    const n = Math.min(src.length, this.out.length);
    const lut = this.lut;
    if (!lut) {
      this.out.set(src.subarray(0, n));
    } else {
      for (let i = 0; i < n; i++) {
        const r = (src[i] ?? 0) & 0xff;
        this.out[i] = lut[r >> 6] ?? 0;
      }
    }
    this.ctx.putImageData(this.image, 0, 0);
  }

  fit(): void {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight || window.innerHeight;
    const scale = Math.max(1, Math.floor(Math.min(w / SCREEN_WIDTH, h / SCREEN_HEIGHT)));
    this.canvas.style.width = `${SCREEN_WIDTH * scale}px`;
    this.canvas.style.height = `${SCREEN_HEIGHT * scale}px`;
  }

  async toggleFullscreen(): Promise<void> {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await this.container.requestFullscreen({ navigationUI: "hide" });
  }

  dispose(): void {
    this.resizeObserver.disconnect();
  }
}
